import { createContext, useCallback, useContext, useState } from 'react'
import { toast } from 'react-toastify'

import { useQuery } from '@tanstack/react-query'

import api from 'services/api'
import {
  ImageUploadPhase,
  optimizeImageForUpload,
  runImageUploadTask
} from 'utils/imageUpload'

interface UserContextData {
  updateUser(data: any): Promise<void>
  getUserData: () => Promise<void>
  listUsers: () => Promise<IUsers[]>
  uploadFile(
    file: File,
    purpose?: string,
    onPhase?: (phase: ImageUploadPhase) => void
  ): Promise<string>
  user: IUser
}

export interface IUsers {
  id: string
  firstname: string
  lastname: string
  about?: string
  profilePicture?: string
  coverPicture?: string
  verified?: boolean
}

export interface IUser {
  follows: []
  id: string
  firstname: string
  lastname: string
  country?: string
  birthdate: string
  email: string
  role?: 'master' | 'admin' | 'publisher' | 'commercial' | 'user'
  isAdmin?: boolean
  isMaster?: boolean
  isPublisher?: boolean
  isCommercial?: boolean
  isStaff?: boolean
  verified?: boolean
  gender?: 'M' | 'F' | 'NB' | 'AN'
  about?: string
  resetPasswordToken?: string
  resetPasswordExpire?: Date
  status: boolean
  ranking?: string
  favoriteItineraries: []
  followers: number
  profilePicture?: string
  coverPicture?: string
  facebook: string
  instagram: string
  interests?: Array<{
    interest: { name: string; id: string; description: string }
  }>
  itineraries?: Array<{
    id: string
    title: string
    coverImage: string
    cities?: Array<{
      id?: string
      name: string
      latitude?: number
      longitude?: number
    }>
  }>
}

const UserContext = createContext<UserContextData>({} as UserContextData)

export const UserProvider = ({ children }: any) => {
  const [user, setUser] = useState<IUser | null>(null)
  const storedToken = window.localStorage.getItem('token')

  const { refetch } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      try {
        const { data }: { data: IUser } = await api.get('/users')
        setUser(data)
        window.localStorage.setItem('user', JSON.stringify(data))
        return data
      } catch (error: any) {
        if (error?.response?.status === 401) {
          window.localStorage.removeItem('token')
          window.localStorage.removeItem('user')
          setUser(null)
        }

        throw error
      }
    },
    enabled: Boolean(storedToken),
    retry: false,
    staleTime: 5 * 60 * 1000
  })

  const getUserData = useCallback(async () => {
    try {
      await refetch()
    } catch (error: any) {
      if (error?.response?.status === 401) {
        toast.warning(
          'Sua sessão expirou. Faça login novamente para continuar.',
          {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
            theme: 'dark'
          }
        )
        return
      }

      toast.warning(
        'Você não está logado, conecte-se para melhores recursos!',
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
          theme: 'dark'
        }
      )
    }
  }, [refetch])

  const listUsers = useCallback(async (): Promise<IUsers[]> => {
    try {
      const { data } = await api.get('/users/all')
      return Array.isArray(data) ? data : []
    } catch (error) {
      toast.error(
        'Não foi possível carregar os usuários, tente novamente mais tarde',
        {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
          theme: 'dark'
        }
      )
      return []
    }
  }, [])

  const updateUser = async (register: any) => {
    try {
      const response = await api.patch('/users', {
        ...register,
        instagram: register.instagram,
        facebook: register.facebook
      })

      toast.success('Conta atualizada com sucesso!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: 'dark'
      })

      const updatedUser = response.data
      setUser(updatedUser)
      window.localStorage.setItem('user', JSON.stringify(updatedUser))

      return updatedUser
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          'Não foi possível editar os dados, tente novamente',
        {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
          theme: 'dark'
        }
      )
      throw e
    }
  }

  const uploadFile = async (
    file: File,
    purpose = 'generic_image',
    onPhase?: (phase: ImageUploadPhase) => void
  ) =>
    runImageUploadTask(async () => {
      try {
        const optimizedFile = await optimizeImageForUpload(file)
        onPhase?.('uploading')

        const formData = new FormData()
        formData.append('file', optimizedFile)
        formData.append('purpose', purpose)

        const response = await api.post('/users/uploadFile', formData)

        if (!response.data?.locationInS3) {
          throw new Error('A API não retornou a URL da imagem enviada')
        }

        onPhase?.('done')
        return response.data.locationInS3
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Não foi possível enviar a imagem.'
        throw new Error(Array.isArray(message) ? message[0] : message)
      }
    }, onPhase)

  const stringifyUser: any = window.localStorage.getItem('user')

  const storageUser =
    stringifyUser && stringifyUser.length > 1
      ? JSON.parse(stringifyUser)
      : false

  return (
    <UserContext.Provider
      value={{
        user: user ?? storageUser,
        getUserData,
        updateUser,
        uploadFile,
        listUsers
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useUser(): UserContextData {
  return useContext(UserContext)
}
